import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FeedbackInviteMode, ProductsApi } from '@up2stay/up2promo-api-client';
import type {
  ProductDto,
  UpdateProductDto,
} from '@up2stay/up2promo-api-client';
import {
  applyRoutingMode,
  buildProductWriteFromFormValues,
  canSubmitProductForm,
  createProductDetailFormSchema,
  productDetailDefaults,
  type ProductDetailFormValues,
  type ProductFormLocation,
  type ProductFormValues,
  type ProductRoutingMode,
} from '@up2stay/up2promo-commons/products';
import type { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { useDebouncedAutosave } from '@/core/hooks/use-debounced-autosave';
import { productsQueryKeys } from '@/core/query-keys/products-query-keys';

const AUTOSAVE_DEBOUNCE_MS = 250;

type UseProductDetailFormOptions = {
  product: ProductDto | null;
  productId: string | undefined;
  workspaceId: string | undefined;
  knownProductType?: string | null;
  locations: ProductFormLocation[];
  messages?: {
    descriptionRequired?: string;
    targetUrlRequired?: string;
    invalidTargetUrl?: string;
    feedbackFormRequired?: string;
    saveError?: string;
    genericError?: string;
    statsResetPrompt?: string;
    statsResetAction?: string;
    statsResetDone?: string;
    statsResetError?: string;
  };
};

/**
 * Fields that change where scans lead. When one of them changes, the old
 * statistics describe a different destination (ADR-0017 §8).
 */
function routingSnapshot(values: Partial<ProductDetailFormValues>): string {
  return JSON.stringify({
    targetKind: values.targetKind ?? null,
    routingMode: values.routingMode ?? null,
    targetUrl: values.targetUrl ?? null,
    locationId: values.locationId ?? null,
    userId: values.userId ?? null,
  });
}

function routingFromSerialized(serialized: string | null): string | null {
  if (!serialized) return null;
  return routingSnapshot(
    JSON.parse(serialized) as Partial<ProductDetailFormValues>,
  );
}

function serializeFormSnapshot(values: ProductDetailFormValues): string {
  return JSON.stringify({
    description: values.description,
    targetKind: values.targetKind,
    locationId: values.locationId,
    userId: values.userId,
    targetUrl: values.targetUrl,
    routingMode: values.routingMode,
    feedbackMode: values.feedbackMode,
    reviewThreshold: values.reviewThreshold,
    feedbackQuestions: values.feedbackQuestions,
  });
}

export function useProductDetailForm(options: UseProductDetailFormOptions) {
  const {
    product,
    productId,
    workspaceId,
    knownProductType,
    locations,
    messages,
  } = options;

  const queryClient = useQueryClient();
  const lastSyncedProductIdRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);
  const locationsRef = useRef(locations);
  const saveRef = useRef<(data: ProductDetailFormValues) => Promise<void>>(
    async () => {},
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Server/committed snapshot — set on product identity hydrate and after save. */
  const [autosaveBaseline, setAutosaveBaseline] = useState<string | null>(null);
  const [baselineProductId, setBaselineProductId] = useState<string | null>(
    null,
  );
  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markIdle,
  } = useAutosaveCue();

  const productSyncId =
    product?.id && productId && product.id === productId ? product.id : null;
  const [appliedProductSyncId, setAppliedProductSyncId] = useState<
    string | null
  >(null);
  const isFormSynced =
    productSyncId !== null && appliedProductSyncId === productSyncId;

  if (productSyncId !== appliedProductSyncId) {
    setAppliedProductSyncId(productSyncId);
    if (productSyncId) {
      setSaveError(null);
    }
  }

  // Capture baseline once per product identity (not on every refetch).
  if (productSyncId !== baselineProductId) {
    setBaselineProductId(productSyncId);
    if (productSyncId && product && product.id === productSyncId) {
      setAutosaveBaseline(
        serializeFormSnapshot(productDetailDefaults(product, knownProductType)),
      );
    } else {
      setAutosaveBaseline(null);
    }
  }

  useEffect(() => {
    locationsRef.current = locations;
  }, [locations]);

  const schema = useMemo(
    () =>
      createProductDetailFormSchema({
        knownProductType,
        locations,
        messages: {
          descriptionRequired: messages?.descriptionRequired,
          targetUrlRequired: messages?.targetUrlRequired,
          invalidTargetUrl: messages?.invalidTargetUrl,
          feedbackFormRequired: messages?.feedbackFormRequired,
        },
      }),
    [
      knownProductType,
      locations,
      messages?.descriptionRequired,
      messages?.targetUrlRequired,
      messages?.invalidTargetUrl,
      messages?.feedbackFormRequired,
    ],
  );

  const form = useForm<ProductDetailFormValues>({
    resolver: zodResolver(schema as never),
    mode: 'onTouched',
    defaultValues: {
      description: '',
      targetKind: 'URL',
      locationId: null,
      userId: null,
      targetUrl: '',
      feedbackMode: FeedbackInviteMode.DIRECT_REVIEW,
      reviewThreshold: 4,
      feedbackQuestions: [],
      routingMode: 'customUrl',
    },
  });

  const { control, reset, setValue, setError, clearErrors, getValues } = form;

  const { errors, touchedFields } = useFormState({
    control,
    name: ['targetUrl', 'description'],
  });

  const watchedValues = useWatch({ control });
  const values = (watchedValues ?? getValues()) as ProductDetailFormValues;
  const serializedValues = serializeFormSnapshot(values);

  useEffect(() => {
    if (!productSyncId || !product || product.id !== productSyncId) {
      lastSyncedProductIdRef.current = null;
      return;
    }

    if (lastSyncedProductIdRef.current === productSyncId) {
      return;
    }

    const defaults = productDetailDefaults(product, knownProductType);
    reset(defaults);
    lastSyncedProductIdRef.current = productSyncId;
  }, [product, productSyncId, knownProductType, reset]);

  const updateProduct = useMutation({
    mutationFn: async (data: ProductDetailFormValues) => {
      if (!workspaceId || !product?.id) {
        throw new Error('Missing data');
      }

      const { routingMode, ...formValues } = data;
      const { label, target, feedback } = buildProductWriteFromFormValues(
        formValues,
        knownProductType,
        routingMode,
      );
      const body: UpdateProductDto = {
        label,
        target,
        ...(feedback && { feedback }),
      };

      return ProductsApi.update({
        path: { workspaceId, id: product.id },
        body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: productsQueryKeys.root,
      });
    },
  });

  const resetStatistics = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !product?.id) {
        throw new Error('Missing data');
      }
      return ProductsApi.update({
        path: { workspaceId, id: product.id },
        body: { resetUsage: true },
      });
    },
    onSuccess: () => {
      toast.success(messages?.statsResetDone ?? 'Statistics reset');
      void queryClient.invalidateQueries({
        queryKey: productsQueryKeys.root,
      });
    },
    onError: () => {
      toast.error(messages?.statsResetError ?? 'Statistics could not be reset');
    },
  });

  const statsResetPrompt = messages?.statsResetPrompt;
  const statsResetAction = messages?.statsResetAction;
  const productIdForToast = product?.id;
  const offerStatisticsReset = useCallback(() => {
    if (!statsResetPrompt) return;
    toast(statsResetPrompt, {
      id: `product-stats-reset-${productIdForToast ?? ''}`,
      duration: 10_000,
      action: {
        label: statsResetAction ?? 'Reset',
        onClick: () => resetStatistics.mutate(),
      },
    });
  }, [statsResetPrompt, statsResetAction, productIdForToast, resetStatistics]);

  const save = useCallback(
    async (data: ProductDetailFormValues) => {
      if (!product?.id || isSavingRef.current) {
        return;
      }
      const routingBefore = routingFromSerialized(autosaveBaseline);

      isSavingRef.current = true;
      setSaveError(null);
      markSaving();

      try {
        await updateProduct.mutateAsync(data);
        setAutosaveBaseline(serializeFormSnapshot(data));
        reset(data, { keepValues: true });
        clearErrors();
        markSaved();
        if (routingBefore !== null && routingBefore !== routingSnapshot(data)) {
          offerStatisticsReset();
        }
      } catch (err) {
        markIdle();
        const axiosError = err as AxiosError<{
          errors?: Record<string, string>;
        }>;
        const targetUrlError = axiosError.response?.data?.errors?.targetUrl;

        if (targetUrlError) {
          setError('targetUrl', {
            type: 'server',
            message: messages?.invalidTargetUrl ?? targetUrlError,
          });
          return;
        }

        const errorMessage =
          err instanceof Error
            ? err.message
            : (messages?.saveError ?? messages?.genericError ?? 'Save failed');
        setSaveError(errorMessage);
        toast.error(errorMessage);
      } finally {
        isSavingRef.current = false;
      }
    },
    [
      product?.id,
      autosaveBaseline,
      offerStatisticsReset,
      updateProduct,
      reset,
      clearErrors,
      setError,
      messages?.invalidTargetUrl,
      messages?.saveError,
      messages?.genericError,
      markSaving,
      markSaved,
      markIdle,
    ],
  );

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const runAutosave = useCallback(() => {
    const data = getValues();
    const { routingMode, ...formValues } = data;

    if (
      !canSubmitProductForm({
        description: data.description,
        values: formValues,
        knownProductType,
        locations: locationsRef.current,
        routingMode,
      })
    ) {
      return;
    }

    void saveRef.current(data);
  }, [getValues, knownProductType]);

  useDebouncedAutosave({
    snapshot: serializedValues,
    baseline: autosaveBaseline,
    enabled: isFormSynced,
    delayMs: AUTOSAVE_DEBOUNCE_MS,
    onSave: runAutosave,
  });

  const patchValues = useCallback(
    (patch: Partial<ProductFormValues>) => {
      for (const [key, value] of Object.entries(patch) as Array<
        [keyof ProductFormValues, ProductFormValues[keyof ProductFormValues]]
      >) {
        const shouldValidate =
          (key === 'targetUrl' && touchedFields.targetUrl) ||
          (key === 'description' && touchedFields.description);
        setValue(key, value, { shouldDirty: true, shouldValidate });
      }
    },
    [setValue, touchedFields.description, touchedFields.targetUrl],
  );

  const setRoutingMode = useCallback(
    (mode: ProductRoutingMode) => {
      const current = getValues();
      setValue('routingMode', mode, { shouldDirty: true });
      patchValues(applyRoutingMode(mode, current, knownProductType));
    },
    [getValues, knownProductType, patchValues, setValue],
  );

  const onTargetUrlBlur = useCallback(() => {
    const targetUrl = getValues('targetUrl');
    setValue('targetUrl', targetUrl, {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [getValues, setValue]);

  const onDescriptionBlur = useCallback(() => {
    const description = getValues('description');
    setValue('description', description, {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [getValues, setValue]);

  const fieldErrors = useMemo(
    () => ({
      description: touchedFields.description
        ? errors.description?.message
        : undefined,
      targetUrl:
        errors.targetUrl?.type === 'server' || touchedFields.targetUrl
          ? errors.targetUrl?.message
          : undefined,
    }),
    [
      touchedFields.description,
      touchedFields.targetUrl,
      errors.description?.message,
      errors.targetUrl?.message,
      errors.targetUrl?.type,
    ],
  );

  return {
    form,
    values,
    isFormSynced,
    isSaving: updateProduct.isPending,
    autosaveStatus,
    saveError,
    fieldErrors,
    patchValues,
    setRoutingMode,
    onTargetUrlBlur,
    onDescriptionBlur,
  };
}
